import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import {
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
} from "../Helper/formbtnhelper.js";
import LaydownCalc from "../Inputs/LaydownCalc.jsx";
import NewDate from "../Inputs/NewDate";
import NewEmail from "../Inputs/NewEmail";
import NewPhone from "../Inputs/NewPhone";
import NewText from "../Inputs/NewText.jsx";
import NewTitle from "../Inputs/NewTitle.jsx";
import RadioGroupField from "../Inputs/RadioGroupField";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper.js";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow.jsx";

// Slide 4: Project Information
const ProjectInformation = ({
  setSlideStatus,
  slideState,
  formStateLoaded,
}) => {
  const [disabledInputs, setDisabledInputs] = useState([]);

  const {
    slide1,
    slide4,
    setSlide4,
    setIsLoading,
    requestStatus,
    names,
    phoneNumbers,
    emails,
    titles,
    companies,
    predictiveTextFetched,
    populateInvalidInputs,
    getInvalidData,
    getInputMessage,
    disabledInputCheck,
  } = useContext(FormContext);

  const { clientId, projectId } = useContext(DashboardContext);

  // On Mount, the page should scroll to the top
  useEffect(() => {
    scrollToFirstRequiredField(slide4);
  }, []);

  // Update Slide State
  const slide4Mutation = useSetSlide(
    clientId,
    projectId,
    4,
    setIsLoading,
    setSlideStatus,
  );

  // Get Invalid Inputs
  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  // Populate Invalid Inputs
  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "4",
    );
  }, [invalidInputsData.data, invalidInputsData.isFetched]);

  return (
    <>
      {formStateLoaded ? (
        <>
          <h2 className="question-section">Project Duration</h2>
          <ResponsiveRow type="center">
            <NewDate
              idProp="startDate"
              label="Start Date"
              state={slide4}
              setState={setSlide4}
              val={slide4.startDate}
              debouncedSave={slide4Mutation}
              setIsLoading={setIsLoading}
              req={
                slide4.startDate === "" ||
                (slide4.endDate !== "" &&
                  dayjs(slide4.startDate).isAfter(slide4.endDate))
              }
              disabledInput={disabledInputCheck("startDate", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("startDate", disabledInputs) === false
                  ? getInputMessage("startDate", invalidInputsData, "4")
                  : ""
              }
              invalidData={getInvalidData("startDate", invalidInputsData, "4")}
            />
            <NewDate
              idProp="endDate"
              label="Estimated End Date"
              state={slide4}
              setState={setSlide4}
              val={slide4.endDate}
              debouncedSave={slide4Mutation}
              setIsLoading={setIsLoading}
              req={
                slide4.endDate === "" ||
                dayjs(slide4.endDate).isBefore(slide4.startDate)
              }
              disabledInput={disabledInputCheck("endDate", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("endDate", disabledInputs) === false
                  ? getInputMessage("endDate", invalidInputsData, "4")
                  : ""
              }
              invalidData={getInvalidData("endDate", invalidInputsData, "4")}
            />
          </ResponsiveRow>
          <h2 className="question-section">24-Hour Emergency Contact</h2>
          <ResponsiveRow>
            <NewText
              idProp="emergencyContactName"
              label="First & Last Name"
              val={slide4.emergencyContactName}
              state={slide4}
              setState={setSlide4}
              debouncedSave={slide4Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? names : []}
              req={
                !isValidName(slide4.emergencyContactName) ||
                slide4.emergencyContactName === ""
              }
              type="name"
              disabledInput={disabledInputCheck(
                "emergencyContactName",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("emergencyContactName", disabledInputs) ===
                false
                  ? getInputMessage(
                      "emergencyContactName",
                      invalidInputsData,
                      "4",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "emergencyContactName",
                invalidInputsData,
                "4",
              )}
            />
            <NewPhone
              idProp="emergencyContactPhone"
              label="Phone"
              val={slide4.emergencyContactPhone}
              state={slide4}
              setState={setSlide4}
              debouncedSave={slide4Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? phoneNumbers : []}
              req={
                !isValidPhoneNumber(slide4.emergencyContactPhone) ||
                slide4.emergencyContactPhone === ""
              }
              disabledInput={disabledInputCheck(
                "emergencyContactPhone",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("emergencyContactPhone", disabledInputs) ===
                false
                  ? getInputMessage(
                      "emergencyContactPhone",
                      invalidInputsData,
                      "4",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "emergencyContactPhone",
                invalidInputsData,
                "4",
              )}
            />
            <NewEmail
              idProp="emergencyContactEmail"
              label="Email Address"
              val={slide4.emergencyContactEmail}
              state={slide4}
              setState={setSlide4}
              debouncedSave={slide4Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? emails : []}
              req={
                !isValidEmail(slide4.emergencyContactEmail) ||
                slide4.emergencyContactEmail === ""
              }
              type="email"
              disabledInput={disabledInputCheck(
                "emergencyContactEmail",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("emergencyContactEmail", disabledInputs) ===
                false
                  ? getInputMessage(
                      "emergencyContactEmail",
                      invalidInputsData,
                      "4",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "emergencyContactEmail",
                invalidInputsData,
                "4",
              )}
            />
            <NewTitle
              idProp="emergencyContactTitle"
              label="Title"
              val={slide4.emergencyContactTitle}
              state={slide4}
              setState={setSlide4}
              debouncedSave={slide4Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? titles : []}
              req={
                slide4.emergencyContactTitle === "" ||
                slide4.emergencyContactTitle.length < 2
              }
              disabledInput={disabledInputCheck(
                "emergencyContactTitle",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("emergencyContactTitle", disabledInputs) ===
                false
                  ? getInputMessage(
                      "emergencyContactTitle",
                      invalidInputsData,
                      "4",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "emergencyContactTitle",
                invalidInputsData,
                "4",
              )}
            />
          </ResponsiveRow>
          {slide1.onNativeLand == "Yes" ? (
            <>
              <RadioGroupField
                idProp="prevAgricultureLand"
                label="Land previously used for agriculture"
                val={slide4.prevAgricultureLand}
                state={slide4}
                setState={setSlide4}
                debouncedSave={slide4Mutation}
                setIsLoading={setIsLoading}
                disabledInput={disabledInputCheck(
                  "prevAgricultureLand",
                  disabledInputs,
                )}
              />
              <RadioGroupField
                idProp="demolitionActivities"
                label="Onsite demolition activities"
                val={slide4.demolitionActivities}
                state={slide4}
                setState={setSlide4}
                debouncedSave={slide4Mutation}
                setIsLoading={setIsLoading}
                disabledInput={disabledInputCheck(
                  "demolitionActivities",
                  disabledInputs,
                )}
              />
              <RadioGroupField
                idProp="dewateringDischarge"
                label="Discharging dewatering water"
                val={slide4.dewateringDischarge}
                state={slide4}
                setState={setSlide4}
                debouncedSave={slide4Mutation}
                setIsLoading={setIsLoading}
                disabledInput={disabledInputCheck(
                  "dewateringDischarge",
                  disabledInputs,
                )}
              />
            </>
          ) : null}
          <h2 className="question-section">Project Size</h2>
          <ResponsiveRow type={"center-gap"}>
            <NewText
              idProp={"totAcr"}
              label="Total Acreage"
              state={slide4}
              setState={setSlide4}
              val={slide4.totAcr}
              setIsLoading={setIsLoading}
              req={slide4.totAcr === ""}
              type="acreage"
              debouncedSave={projectId ? slide4Mutation : undefined}
              disabledInput={disabledInputCheck("totAcr", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("totAcr", disabledInputs) === false
                  ? getInputMessage("totAcr", invalidInputsData, "4")
                  : ""
              }
              invalidData={getInvalidData("totAcr", invalidInputsData, "4")}
            />
            <NewText
              idProp={"distAcr"}
              label="Disturbed Acreage"
              state={slide4}
              setState={setSlide4}
              val={slide4.distAcr}
              setIsLoading={setIsLoading}
              req={slide4.distAcr === ""}
              type="acreage"
              debouncedSave={projectId ? slide4Mutation : undefined}
              disabledInput={disabledInputCheck("distAcr", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("distAcr", disabledInputs) === false
                  ? getInputMessage("distAcr", invalidInputsData, "4")
                  : ""
              }
              invalidData={getInvalidData("distAcr", invalidInputsData, "4")}
            />
          </ResponsiveRow>
          <RadioGroupField
            idProp="layDownYard"
            label="Construction/laydown yard"
            val={slide4.layDownYard}
            state={slide4}
            setState={setSlide4}
            debouncedSave={slide4Mutation}
            setIsLoading={setIsLoading}
            disabledInput={disabledInputCheck("layDownYard", disabledInputs)}
          />
          {slide4.layDownYard === "Yes" ? (
            <>
              <RadioGroupField
                idProp="layDownYardDist"
                label="Is this yard included in the disturbed acreage above?"
                val={
                  slide4.layDownYard === true ? slide4.layDownYardDist : false
                }
                state={slide4}
                setState={setSlide4}
                debouncedSave={slide4Mutation}
                setIsLoading={setIsLoading}
                disabledInput={disabledInputCheck(
                  "layDownYardDist",
                  disabledInputs,
                )}
              />
              {slide4.layDownYardDist === "No" && (
                <LaydownCalc
                  idProp="layDownSize"
                  label="Estimated Yard Size"
                  state={slide4}
                  setState={setSlide4}
                  val={slide4.layDownSize}
                  setIsLoading={setIsLoading}
                  req={false}
                  type="number"
                  debouncedSave={projectId ? slide4Mutation : undefined}
                  disabledInput={disabledInputCheck(
                    "layDownSize",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck("layDownSize", disabledInputs) === false
                      ? getInputMessage("layDownSize", invalidInputsData, "4")
                      : ""
                  }
                  invalidData={getInvalidData(
                    "layDownSize",
                    invalidInputsData,
                    "4",
                  )}
                />
              )}
            </>
          ) : // If No, add a field allowing the user to input the area of the laydown yard, which will be added to the disturbed acreage above
          null}
          <RadioGroupField
            idProp="borrowArea"
            label="Onsite plant or offsite borrow area"
            val={slide4.borrowArea}
            state={slide4}
            setState={setSlide4}
            debouncedSave={slide4Mutation}
            setIsLoading={setIsLoading}
            disabledInput={disabledInputCheck("borrowArea", disabledInputs)}
          />
          <RadioGroupField
            idProp="portableTanksOnSite"
            label="Portable fuel storage tanks"
            val={slide4.portableTanksOnSite}
            state={slide4}
            setState={setSlide4}
            debouncedSave={slide4Mutation}
            setIsLoading={setIsLoading}
            disabledInput={disabledInputCheck(
              "portableTanksOnSite",
              disabledInputs,
            )}
          />
          {slide4.portableTanksOnSite === "Yes" ? (
            <ResponsiveRow>
              <NewText
                idProp="portableTankCapacity"
                label="Tank(s) Capacity"
                state={slide4}
                setState={setSlide4}
                type="number"
                val={slide4.portableTankCapacity}
                debouncedSave={slide4Mutation}
                setIsLoading={setIsLoading}
                disabledInput={disabledInputCheck(
                  "portableTankCapacity",
                  disabledInputs,
                )}
                disabledInputMessage={
                  disabledInputCheck("portableTankCapacity", disabledInputs) ===
                  false
                    ? getInputMessage(
                        "portableTankCapacity",
                        invalidInputsData,
                        "4",
                      )
                    : ""
                }
                invalidData={getInvalidData(
                  "portableTankCapacity",
                  invalidInputsData,
                  "4",
                )}
              />
            </ResponsiveRow>
          ) : null}
          <h2 className="question-section" id="bmp-question-section">
            BMP Company Information
          </h2>
          <p className="important-text">
            Please provide the company name and contact information for the
            company who will be installing & maintaining the BMPs
          </p>
          <ResponsiveRow type="center">
            <NewText
              idProp="bmpCompanyName"
              label="Company Name"
              val={slide4.bmpCompanyName}
              state={slide4}
              setState={setSlide4}
              debouncedSave={slide4Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? companies : []}
              req={
                slide4.bmpCompanyName === "" || slide4.bmpCompanyName.length < 3
              }
              type="company"
              disabledInput={disabledInputCheck(
                "bmpCompanyName",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("bmpCompanyName", disabledInputs) === false
                  ? getInputMessage("bmpCompanyName", invalidInputsData, "4")
                  : ""
              }
              invalidData={getInvalidData(
                "bmpCompanyName",
                invalidInputsData,
                "4",
              )}
            />
            <NewText
              idProp="bmpContactName"
              label="First & Last Name"
              val={slide4.bmpContactName}
              state={slide4}
              setState={setSlide4}
              debouncedSave={slide4Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? names : []}
              req={!isValidName(slide4.bmpContactName, false)}
              type="name"
              disabledInput={disabledInputCheck(
                "bmpContactName",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("bmpContactName", disabledInputs) === false
                  ? getInputMessage("bmpContactName", invalidInputsData, "4")
                  : ""
              }
              invalidData={getInvalidData(
                "bmpContactName",
                invalidInputsData,
                "4",
              )}
            />
            <NewPhone
              idProp="bmpContactPhone"
              label="Phone"
              val={slide4.bmpContactPhone}
              state={slide4}
              setState={setSlide4}
              debouncedSave={slide4Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? phoneNumbers : []}
              req={!isValidPhoneNumber(slide4.bmpContactPhone, false)}
              disabledInput={disabledInputCheck(
                "bmpContactPhone",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("bmpContactPhone", disabledInputs) === false
                  ? getInputMessage("bmpContactPhone", invalidInputsData, "4")
                  : ""
              }
              invalidData={getInvalidData(
                "bmpContactPhone",
                invalidInputsData,
                "4",
              )}
            />
            <NewEmail
              idProp="bmpContactEmail"
              label="Email Address"
              val={slide4.bmpContactEmail}
              state={slide4}
              setState={setSlide4}
              debouncedSave={slide4Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? emails : []}
              req={!isValidEmail(slide4.bmpContactEmail, false)}
              disabledInput={disabledInputCheck(
                "bmpContactEmail",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("bmpContactEmail", disabledInputs) === false
                  ? getInputMessage("bmpContactEmail", invalidInputsData, "4")
                  : ""
              }
              invalidData={getInvalidData(
                "bmpContactEmail",
                invalidInputsData,
                "4",
              )}
            />
          </ResponsiveRow>
        </>
      ) : (
        <p className="loading-text">Loading...</p>
      )}
    </>
  );
};

export default ProjectInformation;
